import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, DollarSign, ArrowRight, Users, ExternalLink, Building2, Clock, Wifi, TrendingUp, AlertTriangle, CheckCircle2, Layers } from "lucide-react";
import { RichTextView } from "@/components/RichTextEditor";

/**
 * Phase 66 → Phase 70 — JobPreviewDialog
 *
 * A modal peek at a job showing ALL details in a compact layout — meta chips
 * (status, location, employment type, work mode, experience level, department,
 * salary, applicant count), full description (scrollable, HTML-rendered),
 * required & nice-to-have skills, must-haves, and red-flags. Used from the
 * feed cards so recruiters can review a full role without leaving the stream.
 */
export default function JobPreviewDialog({ open, onClose, jobId }) {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !jobId) return () => {};
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/jobs/${jobId}`);
                if (alive) setJob(data);
            } catch (err) { toast.error(formatApiError(err)); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [open, jobId]);

    if (!open) return null;

    const statusOpen = job && ["open", "active"].includes((job.status || "").toLowerCase());

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent
                className="rounded-none max-w-3xl w-[92vw] p-0 gap-0 max-h-[90vh] flex flex-col"
                data-testid="job-preview-dialog"
            >
                {/* Sticky header — title + meta chip strip */}
                <div className="p-4 sm:p-5 border-b border-border bg-secondary/30 shrink-0">
                    <DialogTitle asChild>
                        <h2 className="font-display font-bold text-lg sm:text-xl tracking-tight">
                            {loading ? "Loading…" : (job?.title || "Job")}
                        </h2>
                    </DialogTitle>
                    <DialogDescription className="sr-only">Job preview — compact full detail</DialogDescription>
                    {job && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="job-preview-meta">
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                                statusOpen
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                    : "border-border bg-white text-muted-foreground"
                            }`}>{job.status || "—"}</span>
                            {job.department && (
                                <Chip icon={Building2}>{job.department}</Chip>
                            )}
                            {job.employment_type && (
                                <Chip icon={Clock}>{job.employment_type}</Chip>
                            )}
                            {job.work_mode && (
                                <Chip icon={Wifi}>{job.work_mode}</Chip>
                            )}
                            {job.experience_level && (
                                <Chip icon={TrendingUp}>{job.experience_level}</Chip>
                            )}
                            {job.location && (
                                <Chip icon={MapPin}>{job.location}</Chip>
                            )}
                            {(job.min_salary || job.max_salary) && (
                                <Chip icon={DollarSign}>
                                    {job.min_salary || "?"}-{job.max_salary || "?"} {job.salary_currency || ""}
                                </Chip>
                            )}
                            {typeof job.applicant_count === "number" && (
                                <Chip icon={Users}>{job.applicant_count} applicants</Chip>
                            )}
                        </div>
                    )}
                </div>

                {/* Scrollable body — full details in compact sections */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5">
                    {loading ? (
                        <div className="text-sm font-mono text-muted-foreground">Loading job details…</div>
                    ) : !job ? (
                        <div className="text-sm text-muted-foreground">Job not found or you don&apos;t have access.</div>
                    ) : (
                        <>
                            {job.description && (
                                <Section title="Description">
                                    <div className="text-[13px] leading-relaxed jd-readable">
                                        {/^\s*</.test(job.description)
                                            ? <RichTextView html={job.description} />
                                            : <p className="whitespace-pre-wrap">{job.description}</p>}
                                    </div>
                                </Section>
                            )}

                            {(job.required_skills?.length || 0) > 0 && (
                                <Section title="Required skills" icon={Layers}>
                                    <ChipList items={job.required_skills} tint="ikb" />
                                </Section>
                            )}

                            {(job.nice_to_have_skills?.length || 0) > 0 && (
                                <Section title="Nice to have">
                                    <ChipList items={job.nice_to_have_skills} tint="muted" />
                                </Section>
                            )}

                            {(job.must_haves?.length || 0) > 0 && (
                                <Section title="Must-haves" icon={CheckCircle2}>
                                    <ul className="list-disc pl-5 space-y-1 text-[13px]">
                                        {job.must_haves.map((m, i) => <li key={i}>{m}</li>)}
                                    </ul>
                                </Section>
                            )}

                            {(job.red_flags?.length || 0) > 0 && (
                                <Section title="Red flags to avoid" icon={AlertTriangle} tint="destructive">
                                    <ul className="list-disc pl-5 space-y-1 text-[13px] text-destructive">
                                        {job.red_flags.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                </Section>
                            )}
                        </>
                    )}
                </div>

                {/* Sticky footer — actions */}
                {job && (
                    <div className="p-3 sm:p-4 border-t border-border bg-secondary/30 flex items-center justify-between gap-2 flex-wrap shrink-0">
                        {job.slug ? (
                            <a
                                href={`/j/${job.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-ikb inline-flex items-center gap-1"
                                data-testid="job-preview-public-link"
                            >
                                <ExternalLink className="w-3 h-3" /> Public link
                            </a>
                        ) : <span />}
                        <Link
                            to={`/jobs/${job.id}`}
                            className="text-[11px] font-mono uppercase tracking-widest border border-ikb text-white bg-ikb px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[#001f7a]"
                            data-testid="job-preview-open"
                            onClick={onClose}
                        >
                            Open full job <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Chip({ icon: Icon, children }) {
    return (
        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border bg-white text-foreground/70 inline-flex items-center gap-1">
            {Icon && <Icon className="w-3 h-3" />}
            {children}
        </span>
    );
}

function Section({ title, icon: Icon, tint, children }) {
    const titleTint = tint === "destructive" ? "text-destructive" : "text-muted-foreground";
    return (
        <section>
            <h3 className={`text-[10px] font-display font-bold uppercase tracking-widest mb-2 inline-flex items-center gap-1.5 ${titleTint}`}>
                {Icon && <Icon className="w-3 h-3" />}
                {title}
            </h3>
            {children}
        </section>
    );
}

function ChipList({ items, tint = "muted" }) {
    const cls = tint === "ikb"
        ? "text-[10px] font-mono px-1.5 py-0.5 border border-ikb/30 bg-blue-50/50 text-ikb"
        : "text-[10px] font-mono px-1.5 py-0.5 border border-border bg-secondary text-foreground/80";
    return (
        <div className="flex flex-wrap gap-1">
            {items.map((s, i) => <span key={`${s}-${i}`} className={cls}>{s}</span>)}
        </div>
    );
}
